import { useState, useEffect } from 'react';
import { Settings, Package, Users, Clock, AlertCircle, Check, X } from 'lucide-react';
import type { ProductionLine } from '@/types/production';
import type { Product, Employee } from '@/types/tycoon';
import { PRODUCTION_CONSTANTS } from '@/config/gameConstants';

interface ProductionLineCardProps {
  line: ProductionLine;
  products: Product[];
  maxEmployees: number;
  gameDate: { day: number; month: number; year: number };
  onProductChange: (productId: string | null) => void;
  onEmployeeChange: (count: number) => void;
  availableWorkers?: number;
  employees?: Employee[];
}

export function ProductionLineCard({
  line,
  products,
  maxEmployees,
  gameDate,
  onProductChange,
  onEmployeeChange,
  availableWorkers = 0,
  employees = []
}: ProductionLineCardProps) {
  const [isChangingProduct, setIsChangingProduct] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(line.productId);
  const [pendingWorkerCount, setPendingWorkerCount] = useState(line.allocatedEmployees);
  const [isEditingWorkers, setIsEditingWorkers] = useState(false);
  
  // Add state for stable status display with hysteresis
  const [displayStatus, setDisplayStatus] = useState(line.status);
  const [displayPauseReason, setDisplayPauseReason] = useState(line.pauseReason);
  const [statusChangeCounter, setStatusChangeCounter] = useState(0);
  
  // Calculate actual available assembly workers
  const calculateAvailableWorkers = () => {
    const assemblyWorkers = employees.filter(e => e.role === 'assemblyWorker');
    const totalAssemblyWorkers = assemblyWorkers.length;
    
    // Calculate already allocated workers (excluding this line)
    const allocatedElsewhere = 0;
    // This would need to be passed from parent, but for now we'll use availableWorkers prop
    
    return availableWorkers !== undefined ? availableWorkers : totalAssemblyWorkers - allocatedElsewhere;
  };
  
  const actualAvailableWorkers = calculateAvailableWorkers();
  
  useEffect(() => {
    setPendingWorkerCount(line.allocatedEmployees);
  }, [line.allocatedEmployees]);

  // Implement hysteresis for status changes to prevent flicker
  useEffect(() => {
    // Only update display if status has been stable for 3 updates
    if (line.status !== displayStatus || line.pauseReason !== displayPauseReason) {
      if (statusChangeCounter < 3) {
        setStatusChangeCounter(prev => prev + 1);
      } else {
        // Status has been stable, update display
        setDisplayStatus(line.status);
        setDisplayPauseReason(line.pauseReason);
        setStatusChangeCounter(0);
      }
    } else {
      // Status is same as display, reset counter
      setStatusChangeCounter(0);
    }
  }, [line.status, line.pauseReason, displayStatus, displayPauseReason, statusChangeCounter]);

  const getMonthsUntilUnlock = () => {
    if (!line.lastProductChange) return 0;
    
    const currentMonths = gameDate.year * 12 + gameDate.month;
    const changeMonths = line.lastProductChange.year * 12 + line.lastProductChange.month;
    const monthsSinceChange = currentMonths - changeMonths;
    
    return Math.max(0, PRODUCTION_CONSTANTS.productionLineLockMonths - monthsSinceChange);
  };

  const monthsUntilUnlock = getMonthsUntilUnlock();
  const isLocked = line.productId && monthsUntilUnlock > 0;

  const handleProductChange = () => {
    if (isLocked) return;
    
    if (isChangingProduct) {
      if (selectedProductId !== line.productId) {
        onProductChange(selectedProductId);
      }
      setIsChangingProduct(false);
    } else {
      setIsChangingProduct(true);
    }
  };
  
  const handleWorkerSave = () => {
    onEmployeeChange(pendingWorkerCount);
    setIsEditingWorkers(false);
  };
  
  const handleWorkerCancel = () => {
    setPendingWorkerCount(line.allocatedEmployees);
    setIsEditingWorkers(false);
  };

  const getStatusColor = () => {
    // Use display status instead of line status to prevent flicker
    switch (displayStatus) {
      case 'active': return 'bg-theme-success/20 text-theme-success';
      case 'paused': return 'bg-theme-warning/20 text-theme-warning';
      case 'idle': return 'bg-theme-bg-tertiary text-theme-text-primary';
      default: return 'bg-theme-bg-tertiary text-theme-text-primary';
    }
  };

  const getPauseReasonMessage = () => {
    // Use display pause reason instead of line pause reason to prevent flicker
    switch (displayPauseReason) {
      case 'no_product': return 'No product assigned';
      case 'no_employees': return 'No employees assigned';
      case 'no_components': return 'Component shortage';
      case 'maintenance': return 'Under maintenance';
      default: return '';
    }
  };

  return (
    <div className="bg-theme-bg-secondary rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Settings className="w-5 h-5 text-theme-text-muted mr-2" />
          <h4 className="font-medium text-theme-text-primary">Production Line {line.lineNumber}</h4>
          <span className={`ml-3 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor()}`}>
            {displayStatus.toUpperCase()}
          </span>
        </div>
        
        {displayPauseReason && (
          <div className="flex items-center text-sm text-theme-warning">
            <AlertCircle className="w-4 h-4 mr-1" />
            {getPauseReasonMessage()}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Product Selection */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-1">
            <Package className="w-4 h-4 inline mr-1" />
            Product
          </label>
          
          {isChangingProduct ? (
            <div className="flex items-center space-x-2">
              <select
                value={selectedProductId || ''}
                onChange={(e) => setSelectedProductId(e.target.value || null)}
                className="flex-1 px-3 py-2 border border-theme-border rounded-md shadow-sm focus:outline-none focus:ring-theme-accent focus:border-theme-accent text-sm text-theme-text-primary bg-theme-bg-secondary"
              >
                <option value="">No Product</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleProductChange}
                className="px-3 py-2 bg-theme-accent text-white text-sm font-medium rounded-md hover:bg-theme-accent/80"
              >
                ✓
              </button>
              <button
                onClick={() => {
                  setIsChangingProduct(false);
                  setSelectedProductId(line.productId);
                }}
                className="px-3 py-2 bg-theme-bg-tertiary text-theme-text-secondary text-sm font-medium rounded-md hover:bg-theme-border"
              >
                ✕
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-sm text-theme-text-primary">
                {line.productName || 'No product assigned'}
              </span>
              <button
                onClick={handleProductChange}
                disabled={isLocked}
                className={`text-sm ${
                  isLocked
                    ? 'text-theme-text-muted cursor-not-allowed'
                    : 'text-theme-accent hover:text-theme-accent/80 cursor-pointer'
                }`}
              >
                Change
              </button>
            </div>
          )}
          
          {isLocked && (
            <div className="flex items-center mt-1 text-xs text-theme-warning">
              <Clock className="w-3 h-3 mr-1" />
              Locked for {monthsUntilUnlock} more month{monthsUntilUnlock !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Employee Allocation */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-1">
            <Users className="w-4 h-4 inline mr-1" />
            Workers
          </label>
          {isEditingWorkers ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max={Math.min(maxEmployees, line.allocatedEmployees + actualAvailableWorkers)}
                  value={pendingWorkerCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    const maxAllowed = Math.min(maxEmployees, line.allocatedEmployees + actualAvailableWorkers);
                    setPendingWorkerCount(Math.min(Math.max(0, value), maxAllowed));
                  }}
                  className="w-20 px-2 py-1 border border-theme-border rounded-md shadow-sm focus:outline-none focus:ring-theme-accent focus:border-theme-accent text-sm text-theme-text-primary bg-theme-bg-secondary"
                />
                <span className="text-sm text-theme-text-muted">/ {maxEmployees}</span>
                <button
                  onClick={handleWorkerSave}
                  className="p-1 bg-theme-success text-white rounded hover:bg-theme-success/80"
                  title="Save"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={handleWorkerCancel}
                  className="p-1 bg-theme-bg-tertiary text-theme-text-secondary rounded hover:bg-theme-border"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {actualAvailableWorkers === 0 && pendingWorkerCount > line.allocatedEmployees && (
                <p className="text-xs text-theme-error">No assembly workers available</p>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-theme-text-primary">{line.allocatedEmployees}</span>
              <span className="text-sm text-theme-text-muted">/ {maxEmployees}</span>
              <button
                onClick={() => setIsEditingWorkers(true)}
                className="text-sm text-theme-accent hover:text-theme-accent/80"
              >
                Change
              </button>
            </div>
          )}
        </div>

        {/* Output */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-1">
            Daily Output
          </label>
          <div className="flex items-baseline">
            <span className="text-2xl font-semibold text-theme-text-primary">{line.dailyOutput}</span>
            <span className="ml-1 text-sm text-theme-text-muted">units/day</span>
          </div>
          {line.efficiency > 0 && (
            <div className="text-xs text-theme-text-muted mt-1">
              {line.efficiency}% efficiency
            </div>
          )}
        </div>
      </div>
    </div>
  );
}